import { ChevronLeft } from '@untitledui/icons'
import { useLocation } from 'react-router-dom'

import { Avatar } from '@components/designSystem/Avatar'
import AdminNavItem from '@components/navbar/desktop/AdminNavItem'
import AnalyticsNavItem from '@components/navbar/desktop/AnalyticsNavItem'
import {
  type DesktopNavigationContextValue,
  DesktopNavigationProvider
} from '@components/navbar/desktop/DesktopNavigationContext'
import InvestigatorItem from '@components/navbar/desktop/InvestigatorItem'
import NavItem from '@components/navbar/desktop/NavItem'
import PanoramaNavItem from '@components/navbar/desktop/PanoramaNavItem'
import StoreManagementNavItem from '@components/navbar/desktop/StoreManagementNavItem'
import TodoListNavItem from '@components/navbar/desktop/TodoListNavItem'
import WorkflowsNavItem from '@components/navbar/desktop/WorkflowsNavItem'
import { StoreIcon } from '@components/navbar/icons'
import { useCurrentStoreId } from '@hooks/useCurrentStore.ts'
import { useIsTablet } from '@hooks/useMediaQuery.ts'
import useNavigator from '@hooks/useNavigator.ts'
import usePersistedState from '@hooks/usePersistedState.ts'
import { useScan } from '@hooks/useScan.ts'
import { useDayLink } from '@hooks/useSelectedDay'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useCurrentUser } from '@state/user'
import { cx } from '@utils/cx'
import { routesFactory } from '@utils/routes.ts'

import ubicaIcon from './ubica.webp?no-inline'

const transitionDuration = 'duration-200'

function DesktopNavigation() {
  // if we're on a tablet, we collapse the nav by default to have more space available
  // on desktop we have more space, so we can afford to have it expanded by default
  const isTablet = useIsTablet()
  const initiallyExpanded = !isTablet // default to expanded on desktop, collapsed on tablet

  const user = useCurrentUser()
  const { pathname } = useLocation()
  const { t } = useTypedTranslation()
  const [isExpanded, setExpanded] = usePersistedState({ key: 'navbar:expanded', initialState: initiallyExpanded })
  const storeId = useCurrentStoreId()
  const { pathParam: scanId } = useScan()
  const { gotoProductFinder } = useNavigator()
  const dayLink = useDayLink()

  const navClasses = cx(
    `
      relative flex h-dvh flex-col items-start justify-center gap-y-4 border-r border-border-secondary bg-white px-4
      py-6 shadow-sm transition-all
    `,
    isExpanded ? 'w-60' : 'w-19',
    transitionDuration
  )

  const expandedIcon = cx('size-4 transition-transform ease-in-out', isExpanded ? '' : 'scale-x-[-1]', transitionDuration)

  const isLinkActive = (path: string) => pathname.endsWith(path)

  const contextValue: DesktopNavigationContextValue = {
    isExpanded,
    setExpanded,
    isActive: isLinkActive,
    storeId,
    scanId,
    user,
  }

  return (
    <DesktopNavigationProvider value={contextValue}>
      <nav className={navClasses}>
        <button
          aria-label='Toggle Navigation'
          className={`
            absolute top-13 right-[-14px] z-20 flex size-8 cursor-pointer items-center justify-center rounded-full
            bg-white text-fg-primary shadow-md
          `}
          onClick={() => setExpanded(expanded => !expanded)}
        >
          <ChevronLeft className={expandedIcon} />
        </button>

        <img
          src={ubicaIcon}
          alt='Logo'
          className='mt-2 mb-8 ml-2 size-7 cursor-pointer'
          onClick={() => gotoProductFinder()}
        />

        <NavItem
          to={dayLink(routesFactory.productFinder(storeId, scanId))}
          label={t('productFinder.title')}
          isActive={isLinkActive('product-finder')}
          icon={<StoreIcon size={20}/>}
        />

        <TodoListNavItem />
        <AnalyticsNavItem />
        <PanoramaNavItem />

        <InvestigatorItem />
        <WorkflowsNavItem />

        <div className='flex-1'></div>

        <StoreManagementNavItem />

        <AdminNavItem />

        <NavItem
          className='pl-1.5' // adjust padding to center avatar with other nav items
          to={dayLink(routesFactory.account(storeId, scanId))}
          label={user.fullname}
          isActive={isLinkActive('account')}
          icon={<Avatar size='sm' />}
        />

      </nav>
    </DesktopNavigationProvider>
  )
}

export default DesktopNavigation

